import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastCtx = createContext(null);

// ─── Toast item component ─────────────────────────────────────────────────────

const TYPE_STYLES = {
  success: {
    bg:      'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700',
    icon:    <CheckCircle size={18} className="text-white" />,
    iconBg:  'bg-green-500',
    text:    'text-green-900 dark:text-green-100',
  },
  error: {
    bg:      'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700',
    icon:    <AlertCircle size={18} className="text-white" />,
    iconBg:  'bg-red-500',
    text:    'text-red-900 dark:text-red-100',
  },
  info: {
    bg:      'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700',
    icon:    <Info size={18} className="text-white" />,
    iconBg:  'bg-blue-500',
    text:    'text-blue-900 dark:text-blue-100',
  },
};

function ToastItem({ toast, onDismiss }) {
  const s = TYPE_STYLES[toast.type] || TYPE_STYLES.success;

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl border shadow-lg min-w-[260px] max-w-sm ${s.bg}`}
      role="alert"
    >
      {/* Icon bubble */}
      <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${s.iconBg}`}>
        {s.icon}
      </div>

      {/* Message */}
      <p className={`flex-1 text-sm font-semibold leading-snug ${s.text}`}>
        {toast.message}
      </p>

      {/* Dismiss */}
      <button
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ─── Toast container ──────────────────────────────────────────────────────────

function ToastContainer({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 items-center pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className="pointer-events-auto animate-[slideDown_0.25s_ease-out]"
          style={{ animation: 'slideDown 0.25s ease-out' }}
        >
          <ToastItem toast={t} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'success') => {
    const id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3200);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastCtx.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastCtx.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be inside ToastProvider');
  return ctx;
}
